import {
  buffers, channel, eventChannel,
} from 'redux-saga';
import {
  call,
  fork,
  put,
  take,
  takeLatest,
} from 'redux-saga/effects';
import WebworkerPromise from 'webworker-promise';
import { type MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import { convertGeometryToMultiPolygon } from '~/_shared/types/polygon/polygon.utils';
import {
  isDriveTimePolygonSettings, type ProximityModel,
} from '~/_shared/types/proximity/proximity.types';
import {
  type GetDriveTimeEvent, GetDriveTimeEventStatus, registerGetDriveTimeEventBroadcastListener,
} from '~/_shared/utils/api/broadcast.helpers';
import { flatten } from '~/_shared/utils/collections/collections';
import { noop } from '~/_shared/utils/function.helpers';
import { getBoundingBoxOfMultipolygon } from '~/_shared/utils/gis/boundingBox.helpers';
import { PollingTool } from '~/_shared/utils/polling/pollingTool';
import { select } from '~/_shared/utils/saga/effects';
import { notNull } from '~/_shared/utils/typeGuards';
import { type PickAction } from '~/_shared/utils/types/action.type';
import {
  startWorker, Workers,
} from '~/_shared/utils/worker/startWorker';
import type { MapIdState } from '~/store/mapId/mapId.state';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';
import { USER_WEB_SOCKET_BROADCAST_INITIALIZED } from '~/store/userData/userData.actionTypes';
import {
  type DriveTimeErrorResponseMessage,
  type DriveTimeRouteResponseType,
  type DriveTimeRouteResultsResponse,
  type DriveTimeRouteStartResponse,
  type DriveTimeTimeMapRequest,
  getDriveTimeRouteResults,
  getDriveTimeRouteStart,
} from '../../../proximity/proximity.repository';
import { type MapSettingsDataAction } from '../../mapSettings/data/mapSettingsData.action';
import { MAP_SETTINGS_FETCH_DATA_SUCCESS } from '../../mapSettings/data/mapSettingsData.actionTypes';
import { type MapSettingsProximityAction } from '../../mapSettings/proximity/mapSettingsProximity.action';
import {
  MAP_SETTINGS_PROXIMITY_ADD_PROXIMITY,
  MAP_SETTINGS_PROXIMITY_MODIFY_PROXIMITY,
} from '../../mapSettings/proximity/mapSettingsProximity.actionTypes';
import { mapComponentSetZoomToBounds } from '../mapComponent/mapComponent.actionCreators';
import { proximityDriveTimeSetProximityPaths } from './proximityDriveTimePaths.actionCreators';
import { getDriveTimePathsPathId } from './proximityDriveTimePaths.helpers';
import {
  type ProximityDriveTimePathRequest, type ProximityDriveTimePathsState,
} from './proximityDriveTimePaths.state';

type DriveTimeRequest = Readonly<{
  request: DriveTimeTimeMapRequest;
  zoomTo: boolean;
  mapId: number | null;
  pathRequest: ReadonlyArray<ProximityDriveTimePathRequest>;
}>;

const DTP_POLLING_INTERVAL = 3000;

const driveTimeRequests = new Map<string, DriveTimeRequest>();
const pollingChannel = channel<string>();
const pollingBackup = new PollingTool(DTP_POLLING_INTERVAL);

export function* proximityDriveTimePathsSagas() {
  yield takeLatest(MAP_SETTINGS_FETCH_DATA_SUCCESS, onMapSettingsLoaded);
  yield takeLatest(USER_WEB_SOCKET_BROADCAST_INITIALIZED, registerDriveTimeEventsListener);
  yield takeLatest(MAP_SETTINGS_PROXIMITY_ADD_PROXIMITY, onNewProximityAdd);
  yield takeLatest(MAP_SETTINGS_PROXIMITY_MODIFY_PROXIMITY, onMapSettingsProximityListSet);
  yield fork(watchPollingChannel);
}

/**
 * Watch polling channel and trigger polling
 * The `pollingChannel` is required because `yield` effects (like `put` or `call`) cannot be executed directly within
 * the standard JavaScript callback used by `setTimeout`. The channel acts as the necessary conduit back into the
 * Saga's execution context.
 */
function* watchPollingChannel() {
  while (true) {
    const hash: string = yield take(pollingChannel);
    yield fork(onTriggerPolling, hash);
  }
}

function* onTriggerPolling(hash: string) {
  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const requestData = driveTimeRequests.get(hash);

  if (!clientId || !requestData) {
    pollingBackup.remove(hash);
    return;
  }

  try {
    const dtRouteResultsResponse: DriveTimeRouteResultsResponse = yield call(getDriveTimeRouteResults, clientId, requestData.mapId, hash);

    if (dtRouteResultsResponse.data?.length > 0) {
      pollingBackup.remove(hash);
      driveTimeRequests.delete(hash);
      yield call(processDriveTimeRouteResultsResponse, dtRouteResultsResponse.data, requestData);
    }
    else {
      pollingBackup.reschedule(hash);
    }
  }
  catch (e) {
    pollingBackup.remove(hash);
    driveTimeRequests.delete(hash);
    yield call(processDriveTimeRouteResultsResponse, [], requestData);
  }
}

function* onNewProximityAdd(action: PickAction<MapSettingsProximityAction, typeof MAP_SETTINGS_PROXIMITY_ADD_PROXIMITY>) {
  yield processProximityRequests([action.payload.proximity], action.payload.zoomAfter);
}

function* onMapSettingsProximityListSet(action: PickAction<MapSettingsProximityAction, typeof MAP_SETTINGS_PROXIMITY_MODIFY_PROXIMITY>) {
  yield processProximityRequests([action.payload.proximity]);
}

function* onMapSettingsLoaded(action: PickAction<MapSettingsDataAction, typeof MAP_SETTINGS_FETCH_DATA_SUCCESS>) {
  yield processProximityRequests(action.payload.mapSettingsData.proximity?.proximities ?? []);
}

export type ProcessedProximityResult = {
  proximityPathId: string;
  paths?: MultiPolygon | null;
  message?: DriveTimeErrorResponseMessage;
};

function* processProximityRequests(proximities: ProximityModel[], zoomAfter?: boolean) {
  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const mapId: MapIdState = yield select<MapIdState>(mapIdSelector);
  const proximityDriveTimePathsState: ProximityDriveTimePathsState = yield select<ProximityDriveTimePathsState>(
    state => state.frontendState.proximityDriveTimePaths
  );
  if (!clientId) {
    return;
  }

  const requests: ProximityDriveTimePathRequest[] = [];

  for (const proximity of proximities) {
    if (!isDriveTimePolygonSettings(proximity)) {
      continue;
    }

    const request = {
      minutes: proximity.data.minutes,
      hours: proximity.data.hours,
      latLng: {
        lat: proximity.data.lat,
        lng: proximity.data.lng,
      },
    };

    const proximityPathId = getDriveTimePathsPathId(request);

    // skip request if data already exists
    if (proximityDriveTimePathsState.paths[proximityPathId]) {
      continue;
    }

    // skip request if DT path already requested
    if (driveTimeRequests.values().some(dtRequest => dtRequest.pathRequest.some(pathRequest =>
      getDriveTimePathsPathId(pathRequest) === proximityPathId
    ))) {
      continue;
    }

    requests.push(request);
  }

  if (!requests.length) {
    return;
  }

  try {
    const driveTimeRoutes: DriveTimeTimeMapRequest = requests.map((r, index) => ({
      id: index + '',
      longitude: r.latLng.lng,
      latitude: r.latLng.lat,
      transportation: 'driving',
      // minutes can be a decimal value, the endpoint accepts whole seconds only
      travel_time: Math.round((r.hours * 60 * 60) + (r.minutes * 60)),
    }));

    const driveTimeRequestData = {
      request: driveTimeRoutes,
      zoomTo: !!zoomAfter,
      mapId,
      pathRequest: requests,
    };

    const dtRouteStartResponse: DriveTimeRouteStartResponse = yield call(getDriveTimeRouteStart, clientId, mapId, driveTimeRoutes);

    if (dtRouteStartResponse.data) {
      yield call(processDriveTimeRouteResultsResponse, dtRouteStartResponse.data, driveTimeRequestData);
    }
    else if (dtRouteStartResponse.hash) {
      const { hash } = dtRouteStartResponse;
      driveTimeRequests.set(hash, driveTimeRequestData);
      pollingBackup.add(hash, () => pollingChannel.put(hash));
    }
  }
  catch (e) {
    console.error(e);

    yield put(proximityDriveTimeSetProximityPaths(requests.map(r => ({
      success: false,
      proximityPathId: getDriveTimePathsPathId(r),
      paths: null,
      message: 'TRAVEL_TIME_OTHER' as const,
    }))));
  }
}

function* registerDriveTimeEventsListener() {
  const driveTimeEventsChannel: ReturnType<typeof registerDriveTimeBroadcastEventsListener> = yield call(registerDriveTimeBroadcastEventsListener);
  const clientId: number | null = yield select<number | null>(clientIdSelector);

  if (!clientId) {
    return;
  }

  while (true) {
    const e: GetDriveTimeEvent = yield take(driveTimeEventsChannel);

    const requestData = driveTimeRequests.get(e.hash);
    if (!requestData || !clientId) {
      continue;
    }

    pollingBackup.remove(e.hash);
    driveTimeRequests.delete(e.hash);

    let dtRouteResultsResponse: DriveTimeRouteResultsResponse;

    try {
      dtRouteResultsResponse = yield call(getDriveTimeRouteResults, clientId, requestData.mapId, e.hash);
    }
    catch (e) {
      dtRouteResultsResponse = { data: [] };
    }

    yield call(processDriveTimeRouteResultsResponse, dtRouteResultsResponse.data, requestData);
  }
}

const registerDriveTimeBroadcastEventsListener = () => {
  return eventChannel<GetDriveTimeEvent>(emitter => {
    registerGetDriveTimeEventBroadcastListener(
      (e) => emitter(e),
      () => emitter({
        status: GetDriveTimeEventStatus.Failed,
        hash: '',
      })
    );

    return noop;
  }, buffers.expanding());
};

function* processDriveTimeRouteResultsResponse(response: ReadonlyArray<DriveTimeRouteResponseType>, driveTimeRequest: DriveTimeRequest) {
  const dtRouteResultsLookup = new Map(driveTimeRequest.request.map(({ id }, index) => {
    const responseItem = response[index];
    return [id, {
      id,
      ...responseItem ?? {
        success: false,
        message: 'TRAVEL_TIME_OTHER' as const,
        data: undefined,
      },
    }];
  }));

  const dtPolygons: Array<ProcessedProximityResult> = driveTimeRequest.request.map((dtRoute, index) => {
    const travelTimeResultItem = dtRouteResultsLookup.get(dtRoute.id);
    const pathRequest = driveTimeRequest.pathRequest[index];

    if (!pathRequest || !travelTimeResultItem) {
      console.error('Travel time: time-map returned invalid results', { item: travelTimeResultItem, index, result: driveTimeRequest.request });
      return null;
    }

    const proximityPathId = getDriveTimePathsPathId(pathRequest);

    if (!travelTimeResultItem.success || !travelTimeResultItem.data) {
      console.error(travelTimeResultItem.message, { item: travelTimeResultItem, index, result: driveTimeRequest.request });
      return {
        proximityPathId,
        message: travelTimeResultItem.message,
        paths: null,
      };
    }

    return {
      proximityPathId,
      paths: flatten(travelTimeResultItem.data.map(p => convertGeometryToMultiPolygon(p.geometry))),
    };
  }).filter(notNull);

  const worker = new WebworkerPromise(startWorker(Workers.SimplifyDriveTimePaths));
  const simplifiedPolygons: Array<ProcessedProximityResult> = yield call(() => worker.postMessage({ dtData: dtPolygons }));

  const pathsIdArray = simplifiedPolygons.map(item => {
    return {
      proximityPathId: item.proximityPathId,
      paths: item.paths ?? null,
      message: item.message ?? null,
    };
  });

  if (pathsIdArray[0]) {
    yield put(proximityDriveTimeSetProximityPaths(pathsIdArray));

    if (driveTimeRequest.zoomTo && pathsIdArray[0].paths) {
      yield put(mapComponentSetZoomToBounds(getBoundingBoxOfMultipolygon(pathsIdArray[0].paths)));
    }
  }
}
